import React, { useState, useEffect, useCallback } from 'react';
import Checkbox from '../Global/Checkbox';
import whiteLabelService from '../../services/whiteLabelService';
import './scss/InstanceSetup.scss';

/**
 * Component for initial white label instance creation after subscription
 * This component is shown when user has subscription but no white label instance yet
 */
const InstanceSetup = ({ onSubmit, loading, user, paymentStatus }) => {
  const [formData, setFormData] = useState({
    name: '',
    branding: {
      appName: '',
      primaryColor: '#0966ff'
    },
    features: {
      allowPublicSignup: false
    }
  });

  const [errors, setErrors] = useState({});
  const [subdomainStatus, setSubdomainStatus] = useState({
    checking: false,
    available: null,
    suggestions: []
  });
  const [customSubdomain, setCustomSubdomain] = useState('');
  const [useCustomSubdomain, setUseCustomSubdomain] = useState(false);

  // Generate subdomain preview
  const generateSubdomainPreview = (name) => {
    if (!name) return 'your-app';
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '') || 'your-app';
  };

  const subdomainPreview = useCustomSubdomain ? customSubdomain : generateSubdomainPreview(formData.branding.appName);

  // Debounced subdomain validation
  const checkSubdomainAvailability = useCallback(async (subdomain) => {
    if (!subdomain || subdomain === 'your-app') {
      setSubdomainStatus({
        checking: false,
        available: null,
        suggestions: []
      });
      return;
    }

    setSubdomainStatus(prev => ({ ...prev, checking: true }));

    try {
      const response = await whiteLabelService.validateSubdomain(subdomain);
      setSubdomainStatus({
        checking: false,
        available: response.available,
        suggestions: response.suggestions || []
      });
    } catch (error) {
      console.error('Error checking subdomain:', error);
      setSubdomainStatus({
        checking: false,
        available: null,
        suggestions: []
      });
    }
  }, []);

  // Handle suggestion selection
  const handleSuggestionSelect = (suggestion) => {
    setCustomSubdomain(suggestion);
    setUseCustomSubdomain(true);
    // Clear suggestions after selection
    setSubdomainStatus(prev => ({ ...prev, suggestions: [] }));
  };

  // Reset to auto-generated subdomain
  const resetToAutoGenerated = () => {
    setUseCustomSubdomain(false);
    setCustomSubdomain('');
    setSubdomainStatus({
      checking: false,
      available: null,
      suggestions: []
    });
  };

  // Effect to check subdomain when it changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      checkSubdomainAvailability(subdomainPreview);
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [subdomainPreview, checkSubdomainAvailability]);

  // Form validation
  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Instance name is required';
    }

    if (formData.name.length > 50) {
      newErrors.name = 'Instance name must be 50 characters or less';
    }

    if (formData.branding.appName && formData.branding.appName.length > 30) {
      newErrors.appName = 'App name must be 30 characters or less';
    }

    // Check subdomain availability
    if (subdomainStatus.available === false) {
      newErrors.subdomain = 'This subdomain is not available. Please choose a different app name.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Include the selected subdomain in form data
    const submitData = {
      ...formData,
      subdomain: subdomainPreview
    };

    onSubmit(submitData);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const handleBrandingChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      branding: {
        ...prev.branding,
        [field]: value
      }
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const handleFeatureChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      features: {
        ...prev.features,
        [field]: value
      }
    }));
  };

  return (
    <div className="instance-setup">
      <div className="instance-setup__header">
        <h1>Create Your White Label Instance</h1>
        <p>Set up your branded version of Zoobbe with custom domain and colors.</p>

        {paymentStatus && (
          <div className="payment-status-indicator">
            <div className="status-badge success">
              <i className="fas fa-check-circle"></i>
              White Label Addon Active ({paymentStatus.billingCycle})
            </div>
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit} className="setup-form">
        <div className="section">
          <h3>Basic Information</h3>

          <div className="form-group">
            <label htmlFor="instanceName">Instance Name *</label>
            <input
              id="instanceName"
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="My Company Workspace"
              className={errors.name ? 'error' : ''}
              required
            />
            {errors.name && <span className="error-text">{errors.name}</span>}
            <small>This will be the internal name for your white label instance</small>
          </div>

          <div className="form-group">
            <label htmlFor="appName">App Name *</label>
            <input
              id="appName"
              type="text"
              value={formData.branding.appName}
              onChange={(e) => handleBrandingChange('appName', e.target.value)}
              placeholder="Your App Name"
              className={errors.appName ? 'error' : ''}
              required
            />
            {errors.appName && <span className="error-text">{errors.appName}</span>}
            <small>This will be displayed in the app header and used to generate your subdomain</small>
          </div>

          <div className="form-group">
            <label>Choose Your Subdomain</label>

            {/* Main subdomain input/display */}
            <div className="subdomain-input-container">
              <div className="subdomain-prefix">https://</div>
              <div className="subdomain-main">
                <input
                  type="text"
                  value={subdomainPreview}
                  onChange={(e) => {
                    setCustomSubdomain(e.target.value);
                    setUseCustomSubdomain(true);
                  }}
                  placeholder="your-app"
                  className={`subdomain-input ${subdomainStatus.available === false ? 'error' : ''} ${subdomainStatus.available === true ? 'success' : ''}`}
                />
                <div className="subdomain-status-icon">
                  {subdomainStatus.checking && (
                    <i className="fas fa-spinner fa-spin"></i>
                  )}
                  {!subdomainStatus.checking && subdomainStatus.available === true && (
                    <i className="fas fa-check-circle success"></i>
                  )}
                  {!subdomainStatus.checking && subdomainStatus.available === false && (
                    <i className="fas fa-times-circle error"></i>
                  )}
                </div>
              </div>
              <div className="subdomain-suffix">.zoobbe.com</div>
            </div>

            {/* Status message */}
            <div className="subdomain-status-message">
              {subdomainStatus.checking && (
                <span className="checking">Checking availability...</span>
              )}
              {!subdomainStatus.checking && subdomainStatus.available === true && (
                <span className="available">
                  <i className="fas fa-check"></i> {subdomainPreview}.zoobbe.com is available
                </span>
              )}
              {!subdomainStatus.checking && subdomainStatus.available === false && (
                <span className="unavailable">
                  <i className="fas fa-times"></i> {subdomainPreview}.zoobbe.com is not available
                </span>
              )}
            </div>

            {/* Suggestions */}
            {!subdomainStatus.checking && subdomainStatus.available === false && subdomainStatus.suggestions.length > 0 && (
              <div className="subdomain-suggestions">
                <div className="suggestions-header">
                  <i className="fas fa-lightbulb"></i>
                  <span>Try these available alternatives:</span>
                </div>
                <div className="suggestions-list">
                  {subdomainStatus.suggestions.map((suggestion, index) => (
                    <button
                      key={index}
                      type="button"
                      className="suggestion-item"
                      onClick={() => handleSuggestionSelect(suggestion)}
                    >
                      <span className="suggestion-url">
                        {suggestion}.zoobbe.com
                      </span>
                      <i className="fas fa-check-circle"></i>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Reset option */}
            {useCustomSubdomain && (
              <div className="subdomain-reset">
                <button
                  type="button"
                  className="reset-button"
                  onClick={resetToAutoGenerated}
                >
                  <i className="fas fa-undo"></i>
                  Use auto-generated subdomain
                </button>
              </div>
            )}

            {errors.subdomain && <span className="error-text">{errors.subdomain}</span>}
            <small>Choose a unique subdomain for your white label instance. This will be your custom URL.</small>
          </div>
        </div>

        <div className="section">
          <h3>Initial Settings</h3>

          <div className="form-group">
            <label>Public Access</label>
            <div className="checkbox-wrapper">
              <Checkbox
                checked={formData.features.allowPublicSignup}
                onChange={(checked) => handleFeatureChange('allowPublicSignup', checked)}
                label="Allow public signup"
              />
            </div>
            <small>When enabled, anyone can create an account using your custom domain. You can change this later.</small>
          </div>

          <div className="form-group">
            <label htmlFor="primaryColor">Primary Color</label>
            <div className="color-input">
              <input
                id="primaryColor"
                type="color"
                value={formData.branding.primaryColor}
                onChange={(e) => handleBrandingChange('primaryColor', e.target.value)}
              />
              <input
                type="text"
                value={formData.branding.primaryColor}
                onChange={(e) => handleBrandingChange('primaryColor', e.target.value)}
                placeholder="#0966ff"
              />
            </div>
            <small>Choose your brand's primary color. This will be used for buttons, links, and accents.</small>
          </div>
        </div>

        <div className="actions">
          <button type="submit" disabled={loading} className="btn">
            {loading ? (
              <>
                <span className="spinner-small"></span>
                Creating Instance...
              </>
            ) : (
              'Create White Label Instance'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default InstanceSetup;
